/**
 * "My data" for dashboard widgets: the signed-in user's own accounts from My
 * Portfolio (Alpaca, Coinbase, Interactive Brokers, NinjaTrader via Tradovate,
 * CSV imports) and their live Trade Watcher accounts.
 *
 * Built on the existing My Portfolio endpoints (/api/vector-dashboard/clients*,
 * Pro, always scoped to the signed-in user), so it works with or without a
 * company. Adds an "All my accounts" view that combines every account.
 *
 * An account is identified by its dataSeriesName ("ALPACA|<userId>|<accountId>").
 * Account choices in widget settings: 'all', 'broker:<BROKER>', or one dataSeriesName.
 */
import {
  fetchClients,
  fetchClientPerformance,
  fetchClientTrades,
  fetchClientPositions,
  fetchBrokerStatus,
} from '../../../services/vectorDashboardApi';
import { API_ENDPOINTS } from '../../../config';
import { getBearerAuthHeaders } from '../../../utils/authFetchHeaders';

export const ALL_ACCOUNTS = 'all';
export const MY_RANGE_OPTIONS = [
  { value: '1M', label: 'Last month' },
  { value: '3M', label: 'Last 3 months' },
  { value: '6M', label: 'Last 6 months' },
  { value: 'YTD', label: 'Year to date' },
  { value: '1Y', label: 'Last year' },
  { value: 'All', label: 'All time' },
];

const BROKER_LABEL = {
  ALPACA: 'Alpaca',
  COINBASE: 'Coinbase',
  IBKR: 'Interactive Brokers',
  NINJA: 'NinjaTrader',
  SNAPTRADE: 'Linked brokerage',
};

/** Thrown when the account isn't on Pro (My Portfolio data is a Pro feature). */
export class ProRequiredError extends Error {
  constructor() {
    super('Your own account data is part of RiskLo Pro.');
    this.requiresPro = true;
  }
}

function asProError(err) {
  if (/requires a RiskLo Pro|requiresPro|403/i.test(String(err?.message || ''))) return new ProRequiredError();
  return err;
}

function brokerOf(dataSeriesName) {
  return String(dataSeriesName || '').split('|')[0].toUpperCase();
}

/* Accounts ------------------------------------------------------------- */

/** "Robinhood Crypto ••8300 (sam@x.com)" → "Robinhood Crypto ••8300": the owner is always you. */
export function cleanAccountName(name) {
  return String(name || '').replace(/\s*\([^()]*@[^()]*\)\s*$/, '').trim() || String(name || '');
}

/** Display name for a broker key ("ALPACA" → "Alpaca"; "Robinhood" stays). */
export function brokerLabel(broker) {
  return BROKER_LABEL[broker] || broker || 'Broker';
}

let accountsPromise = null;
let accountsCache = null;

/** The user's accounts (cached for the session; call refreshMyAccounts after connecting one). */
export function loadMyAccounts() {
  if (!accountsPromise) {
    accountsPromise = fetchClients(false)
      .then((data) => {
        accountsCache = (data.clients || []).map((c) => {
          const source = brokerOf(c.dataSeriesName) || String(c.broker || '').toUpperCase();
          return {
            id: c.dataSeriesName,
            name: cleanAccountName(c.name || c.dataSeriesName),
            // Linked brokerages (SnapTrade) go by their own name: Robinhood, Schwab…
            broker: source === 'SNAPTRADE' && c.broker ? c.broker : source,
            source,
            mode: c.mode || null,
          };
        });
        return accountsCache;
      })
      .catch((err) => {
        accountsPromise = null;
        throw asProError(err);
      });
  }
  return accountsPromise;
}

export function refreshMyAccounts() {
  accountsPromise = null;
  accountsCache = null;
  seriesCache.clear();
  return loadMyAccounts();
}

/** Account choices for widget settings (synchronous: uses what loadMyAccounts loaded). */
export function myAccountOptions() {
  const list = accountsCache || [];
  const brokers = [...new Set(list.map((a) => a.broker).filter(Boolean))];
  return [
    { value: ALL_ACCOUNTS, label: 'All my accounts' },
    ...(brokers.length > 1 ? brokers.map((b) => ({ value: `broker:${b}`, label: `All ${BROKER_LABEL[b] || b} accounts` })) : []),
    ...list.map((a) => ({ value: a.id, label: `${a.name} · ${BROKER_LABEL[a.broker] || a.broker}` })),
  ];
}

export function describeAccountChoice(choice) {
  if (!choice || choice === ALL_ACCOUNTS) return 'All my accounts';
  if (choice.startsWith('broker:')) {
    const b = choice.slice(7);
    return `All ${BROKER_LABEL[b] || b} accounts`;
  }
  const a = (accountsCache || []).find((x) => x.id === choice);
  return a ? `${a.name} · ${BROKER_LABEL[a.broker] || a.broker}` : 'One account';
}

/** Accounts an account choice covers, from what's already loaded (for "pulling from" labels). */
export function accountsInChoice(choice) {
  const list = accountsCache || [];
  if (!choice || choice === ALL_ACCOUNTS) return list;
  if (choice.startsWith('broker:')) return list.filter((a) => a.broker === choice.slice(7));
  return list.filter((a) => a.id === choice);
}

/** Headline numbers for one account's own curve (per-account breakdowns). */
export function summarizeAccount(points = []) {
  const daily = dailyPnlFromEquity(points);
  return summarizeDaily(points, daily);
}

async function accountsFor(choice) {
  const list = await loadMyAccounts();
  if (!choice || choice === ALL_ACCOUNTS) return list;
  if (choice.startsWith('broker:')) return list.filter((a) => a.broker === choice.slice(7));
  return list.filter((a) => a.id === choice);
}

/* Equity and daily P&L ----------------------------------------------------- */

/**
 * Combine several accounts' daily equity into one series: on each date, each
 * account's last known equity (carried forward), summed. Net deposits are summed
 * the same day, so daily P&L excludes money moved in or out.
 */
export function combineEquitySeries(seriesList) {
  const dates = [...new Set(seriesList.flatMap((s) => s.map((p) => p.date)))].sort();
  const last = seriesList.map(() => null);
  const byDate = seriesList.map((s) => new Map(s.map((p) => [p.date, p])));
  const floors = seriesList.map((s) => fundingFloor(s));
  return dates.map((date, d) => {
    let equity = 0;
    let netFlow = 0;
    let any = false;
    byDate.forEach((m, i) => {
      const p = m.get(date);
      if (p) {
        const value = Number(p.value) || 0;
        // An account that starts part-way through the period, or grows from a
        // near-empty balance, brings its money in: that's a deposit, not P&L.
        if (d > 0 && (last[i] == null || last[i] < floors[i])) netFlow += value - (last[i] || 0);
        else netFlow += Number(p.netFlow) || 0;
        last[i] = value;
      }
      if (last[i] != null) { equity += last[i]; any = true; }
    });
    return any ? { date, value: equity, netFlow } : null;
  }).filter(Boolean);
}

/*
 * A day that starts from (almost) nothing is the account being funded or its
 * history starting, not trading: e.g. $0.53 → $4,186 the day a brokerage was
 * linked. Below this share of the period's peak (or $10) the day isn't P&L.
 */
const FUNDING_BASE_SHARE = 0.01;
const FUNDING_BASE_MIN = 10;
export function fundingFloor(points) {
  const peak = Math.max(0, ...points.map((p) => Number(p.value) || 0));
  return Math.max(FUNDING_BASE_MIN, peak * FUNDING_BASE_SHARE);
}

/**
 * Daily P&L from equity: today's change minus money moved in or out today.
 * Days that start below the funding floor are left out (see above). Each day
 * keeps its starting balance (`base`) for the time-weighted return.
 */
export function dailyPnlFromEquity(points) {
  const out = [];
  const floor = fundingFloor(points);
  for (let i = 1; i < points.length; i += 1) {
    const base = Number(points[i - 1].value) || 0;
    if (base < floor) continue;
    const pnl = (Number(points[i].value) || 0) - base - (Number(points[i].netFlow) || 0);
    out.push({ date: points[i].date, pnl: Math.round(pnl * 100) / 100, base });
  }
  return out;
}

/** Time-weighted return (0.05 = 5%): each day's P&L over that day's starting balance, chained. */
export function timeWeightedReturn(daily) {
  let growth = 1;
  let any = false;
  daily.forEach((d) => {
    if (!(d.base > 0)) return;
    growth *= 1 + d.pnl / d.base;
    any = true;
  });
  return any ? growth - 1 : null;
}

/** Headline numbers from daily P&L and equity. */
export function summarizeDaily(points, daily) {
  const total = daily.reduce((s, d) => s + d.pnl, 0);
  const wins = daily.filter((d) => d.pnl > 0);
  const losses = daily.filter((d) => d.pnl < 0);
  let peak = -Infinity;
  let maxDd = 0;
  let running = 0;
  daily.forEach((d) => {
    running += d.pnl;
    peak = Math.max(peak, running);
    maxDd = Math.min(maxDd, running - peak);
  });
  const today = new Date().toISOString().slice(0, 10);
  const lastDay = daily[daily.length - 1];
  const grossWin = wins.reduce((s, d) => s + d.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((s, d) => s + d.pnl, 0));
  const best = daily.length ? daily.reduce((a, b) => (b.pnl > a.pnl ? b : a)) : null;
  const worst = daily.length ? daily.reduce((a, b) => (b.pnl < a.pnl ? b : a)) : null;
  const startEquity = points.length ? Number(points[0].value) || 0 : 0;
  const peakEquity = points.length ? Math.max(...points.map((p) => Number(p.value) || 0)) : 0;
  return {
    netPnl: total,
    todayPnl: lastDay && lastDay.date === today ? lastDay.pnl : 0,
    lastDayPnl: lastDay ? lastDay.pnl : 0,
    lastDay: lastDay ? lastDay.date : null,
    equity: points.length ? Number(points[points.length - 1].value) || 0 : 0,
    winDayRate: daily.length ? wins.length / (wins.length + losses.length || 1) : 0,
    bestDay: daily.length ? Math.max(...daily.map((d) => d.pnl)) : 0,
    worstDay: daily.length ? Math.min(...daily.map((d) => d.pnl)) : 0,
    maxDrawdown: maxDd,
    profitFactor: grossLoss ? grossWin / grossLoss : (grossWin ? Infinity : 0),
    tradingDays: wins.length + losses.length,
    // Context for the KPI cards.
    winDays: wins.length,
    lossDays: losses.length,
    grossWin,
    grossLoss,
    bestDate: best ? best.date : null,
    worstDate: worst ? worst.date : null,
    startEquity,
    peakEquity,
    // Time-weighted, so deposits and a near-empty starting balance can't inflate it.
    returnPct: daily.some((d) => d.base != null) ? timeWeightedReturn(daily) : (startEquity > 0 ? total / startEquity : null),
  };
}

/*
 * Loading a dashboard asks for many accounts at once (every widget, every
 * account). The server sheds load under a burst, and a dropped request used
 * to vanish silently: "All my accounts" quietly became two accounts. So:
 * at most a few requests in flight, one shared request per account + period,
 * a retry on failure, and partial results are reported, never cached.
 */
const MAX_IN_FLIGHT = 4;
let inFlight = 0;
const waiting = [];
function throttled(fn) {
  return new Promise((resolve, reject) => {
    const run = () => {
      inFlight += 1;
      fn().then(resolve, reject).finally(() => {
        inFlight -= 1;
        const next = waiting.shift();
        if (next) next();
      });
    };
    if (inFlight < MAX_IN_FLIGHT) run(); else waiting.push(run);
  });
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function withRetry(fn, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await throttled(fn);
    } catch (err) {
      lastErr = err;
      if (asProError(err).requiresPro) throw err;
      // eslint-disable-next-line no-await-in-loop
      if (i < attempts - 1) await sleep(500 * (i + 1) + Math.random() * 300);
    }
  }
  throw lastErr;
}

const seriesCache = new Map(); // `${id}|${range}` → { at, promise }
const SERIES_TTL_MS = 60000;

/** One account's daily values for a period, shared by every widget asking for it. */
function accountSeries(id, range) {
  const key = `${id}|${range}`;
  const hit = seriesCache.get(key);
  if (hit && Date.now() - hit.at < SERIES_TTL_MS) return hit.promise;
  const promise = withRetry(() => fetchClientPerformance(id, range))
    .then((r) => (Array.isArray(r.data) ? r.data : []).map((p) => ({ date: String(p.date).slice(0, 10), value: p.value, netFlow: p.netFlow })))
    .catch((err) => { seriesCache.delete(key); throw err; });
  seriesCache.set(key, { at: Date.now(), promise });
  return promise;
}

/** Forget cached account data (after a sync or linking a brokerage). */
export function clearMyDataCache() {
  seriesCache.clear();
}

/** Equity, daily P&L and headline numbers for an account choice and date range. */
export async function getMyPerformance(choice = ALL_ACCOUNTS, range = '3M') {
  try {
    const accounts = await accountsFor(choice);
    if (!accounts.length) return { accounts: [], points: [], daily: [], perAccount: [], missing: [], summary: summarizeDaily([], []) };
    const settled = await Promise.allSettled(accounts.map((a) => accountSeries(a.id, range)));
    if (settled.every((r) => r.status === 'rejected')) throw settled[0].reason;
    const loaded = [];
    const missing = [];
    settled.forEach((r, i) => {
      if (r.status === 'fulfilled') loaded.push({ account: accounts[i], points: r.value });
      else missing.push(accounts[i]);
    });
    const series = loaded.map((l) => l.points);
    const points = series.length === 1 ? series[0] : combineEquitySeries(series);
    const daily = dailyPnlFromEquity(points);
    // Each account's own curve, for the "By account" view.
    const perAccount = loaded.map(({ account: a, points: p }) => ({ id: a.id, name: a.name, broker: a.broker, points: p }))
      .filter((a) => a.points.length);
    return { accounts, points, daily, perAccount, missing, summary: summarizeDaily(points, daily) };
  } catch (err) {
    throw asProError(err);
  }
}

/** Latest equity per account (for allocation). */
export async function getMyAccountBalances(range = '1M') {
  const accounts = await loadMyAccounts().catch((err) => { throw asProError(err); });
  const rows = await Promise.all(accounts.map(async (a) => {
    try {
      const pts = await accountSeries(a.id, range);
      return { ...a, equity: pts.length ? Number(pts[pts.length - 1].value) || 0 : 0 };
    } catch (_err) {
      return { ...a, equity: 0, missing: true };
    }
  }));
  return rows;
}

/* Trades and positions ---------------------------------------------------- */

export async function getMyTrades(choice = ALL_ACCOUNTS, range = '3M') {
  try {
    const accounts = await accountsFor(choice);
    const lists = await Promise.all(accounts.map((a) => withRetry(() => fetchClientTrades(a.id, range))
      .then((r) => (r.trades || []).map((t) => ({ ...t, account: a.name, broker: a.broker })))
      .catch(() => [])));
    return lists.flat().sort((x, y) => String(y.exitDate || y.entryDate || '').localeCompare(String(x.exitDate || x.entryDate || '')));
  } catch (err) {
    throw asProError(err);
  }
}

export async function getMyPositions(choice = ALL_ACCOUNTS) {
  try {
    const accounts = await accountsFor(choice);
    const lists = await Promise.all(accounts.map((a) => withRetry(() => fetchClientPositions(a.id))
      .then((r) => ({
        account: a,
        supported: r.supportsPerSymbolPositions !== false,
        positions: (r.positions || []).map((p) => ({ ...p, account: a.name, broker: a.broker })),
      }))
      .catch(() => ({ account: a, supported: false, positions: [] }))));
    return {
      positions: lists.flatMap((l) => l.positions),
      unsupported: lists.filter((l) => !l.supported).map((l) => l.account.name),
    };
  } catch (err) {
    throw asProError(err);
  }
}

/* Connections and live accounts --------------------------------------------- */

/** Which sources are connected (pro: false when the account isn't on Pro). */
export async function getMyConnections(email = '') {
  const out = {
    alpaca: false, coinbase: false, ibkr: false, ninjatrader: false, snaptrade: false, tradeWatcher: false, pro: true, linkedInstitutions: [],
  };
  try {
    const status = await fetchBrokerStatus();
    const b = status.brokers || {};
    out.alpaca = Boolean(b.alpaca?.configured);
    out.coinbase = Boolean(b.coinbase?.configured);
    out.ibkr = Boolean(b.ibkr?.configured || b.ib?.configured);
    out.ninjatrader = Boolean(b.ninjatrader?.configured);
    out.snaptrade = Boolean(b.snaptrade?.configured);
    // Brokerages linked through SnapTrade, by name ("Robinhood", "Schwab").
    out.linkedInstitutions = [...new Set((b.snaptrade?.accounts || []).map((x) => x.institution).filter(Boolean))];
  } catch (err) {
    if (asProError(err).requiresPro) out.pro = false;
  }
  try {
    const live = await getMyLiveAccounts(email);
    out.tradeWatcher = live.length > 0;
  } catch (_e) { /* no feed yet */ }
  return out;
}

/**
 * The user's own Trade Watcher accounts (live day P&L, connected, naked). Support
 * and admins get everyone's board, so it's narrowed to accounts that are theirs.
 */
export async function getMyLiveAccounts(email = '') {
  const res = await fetch(API_ENDPOINTS.tradeWatcherBoard, { headers: getBearerAuthHeaders() });
  const data = await res.json().catch(() => ({}));
  if (res.status === 403) throw new ProRequiredError();
  if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t load live accounts');
  const rows = data.live || [];
  if (data.viewer?.scope !== 'all') return rows;
  const me = String(email || '').trim().toLowerCase();
  if (!me) return [];
  return rows.filter((r) => [r.owner, r.ownerEmail, ...(r.linkedEmails || [])]
    .some((e) => String(e || '').toLowerCase() === me));
}

export { BROKER_LABEL };
